import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.182.0/build/three.module.js';

(function () {
  const canvas = document.getElementById('global-bg-canvas');
  const bgWrap = document.querySelector('.global-canvas-bg');

  if (!canvas || !bgWrap || canvas.dataset.skylrkGlobalBgInit === 'true') return;

  canvas.dataset.skylrkGlobalBgInit = 'true';

  const defaultColor = '#305cde';

  const colorMap = {
    black: '#111111',
    white: '#f4f4f4',
    blue: '#305cde',
    navy: '#5E8FB0',
    red: '#c0392b',
    green: '#2e8b57',
    yellow: '#d4ac0d',
    pink: '#d16ba5',
    purple: '#7d3c98',
    orange: '#d97706',
    beige: '#d6c1a3',
    brown: '#7b4f2a',
    grey: '#7f8c8d',
    gray: '#7f8c8d'
  };

  const resolveColor = (color) => {
    if (!color || !String(color).trim()) return null;

    const c = String(color).toLowerCase().trim();

    if (c.startsWith('#') || c.startsWith('rgb') || c.startsWith('hsl')) return c;

    return colorMap[c] || null;
  };

  const clamp01 = (value) => Math.max(0, Math.min(1, value));

  const hexToRgb = (hex) => {
    const normalized = String(hex || '').replace('#', '').trim();

    const value = normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized;

    if (value.length !== 6) return [0.2157, 0.7020, 1.0];

    const int = parseInt(value, 16);

    return [
      ((int >> 16) & 255) / 255,
      ((int >> 8) & 255) / 255,
      (int & 255) / 255,
    ];
  };

  const rgbToHsl = (r, g, b) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;

      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
          break;
      }

      h /= 6;
    }

    return [h, s, l];
  };

  const hue2rgb = (p, q, t) => {
    let temp = t;

    if (temp < 0) temp += 1;
    if (temp > 1) temp -= 1;
    if (temp < 1 / 6) return p + (q - p) * 6 * temp;
    if (temp < 1 / 2) return q;
    if (temp < 2 / 3) return p + (q - p) * (2 / 3 - temp) * 6;

    return p;
  };

  const hslToRgb = (h, s, l) => {
    if (s === 0) return [l, l, l];

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    return [
      hue2rgb(p, q, h + 1 / 3),
      hue2rgb(p, q, h),
      hue2rgb(p, q, h - 1 / 3),
    ];
  };

  const adjustHsl = (rgb, hShift, sScale, lScale) => {
    const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);

    return hslToRgb(
      (h + hShift + 1) % 1,
      clamp01(s * sScale),
      clamp01(l * lScale)
    );
  };

  const buildGradientColors = (hex) => {
    const base = hexToRgb(hex);

    return {
      top: adjustHsl(base, 0.0, 0.95, 1.08),
      bottom: adjustHsl(base, 0.00, 0.82, 0.98),
      accent: adjustHsl(base, 0.00, 1.05, 1.28),
      dark: adjustHsl(base, -0.01, 0.96, 0.25),
    };
  };

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance'
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

  const sceneMain = new THREE.Scene();
  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const plane = new THREE.PlaneGeometry(2, 2);

  const vertexShader = `
    varying vec2 vTextureCoord;

    void main() {
      vTextureCoord = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const lavaLampFragmentShader = `
    precision highp float;

    uniform float uTime;
    uniform vec2 uResolution;
    uniform vec3 uTopColor;
    uniform vec3 uBottomColor;
    uniform vec3 uAccentColor;
    uniform vec3 uDarkColor;
    uniform vec2 uFocusPoint;
    uniform float uFocusStrength;

    varying vec2 vTextureCoord;

    #define filmGrainIntensity 0.1

    mat2 Rot(float a) {
      float s = sin(a);
      float c = cos(a);
      return mat2(c, -s, s, c);
    }

    vec2 hash(vec2 p) {
      p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));
      return fract(sin(p) * 43758.5453);
    }

    float noise(in vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);

      float n = mix(
        mix(
          dot(-1.0 + 2.0 * hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
          dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)),
          u.x
        ),
        mix(
          dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
          dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)),
          u.x
        ),
        u.y
      );

      return 0.5 + 0.5 * n;
    }

    float filmGrainNoise(in vec2 uv) {
      return length(hash(vec2(uv.x, uv.y)));
    }

    void main() {
      vec2 uv = vTextureCoord;
      float aspectRatio = uResolution.x / uResolution.y;
      vec2 tuv = uv - 0.5;
      float t = uTime * 0.5;

      float degree = noise(vec2(t * 0.05, tuv.x * tuv.y));

      tuv.y *= 1.0 / aspectRatio;
      tuv *= Rot(radians((degree - 0.5) * 720.0 + 180.0));
      tuv.y *= aspectRatio;

      float frequency = 5.0;
      float amplitude = 30.0;
      float speed = t * 2.0;

      tuv.x += sin(tuv.y * frequency + speed) / amplitude;
      tuv.y += sin(tuv.x * frequency * 1.5 + speed) / (amplitude * 0.5);

      vec3 color1 = uTopColor;
      vec3 color2 = uDarkColor;
      vec3 color3 = uAccentColor;
      vec3 color4 = uBottomColor;

      vec3 layer1 = mix(color3, color2, smoothstep(-0.38, 0.22, (tuv * Rot(radians(-5.0))).x));
      vec3 layer2 = mix(color4, color1, smoothstep(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
      vec3 flatColor = mix(layer1, layer2, smoothstep(0.5, -0.3, tuv.y));

      vec2 warp = tuv - (uv - 0.5);
      vec2 focusPt = uFocusPoint - 0.5;
      vec2 delta = (uv - 0.5) - focusPt + warp * 0.5;

      delta.x *= aspectRatio;

      float d = length(delta);
      float radialMask = smoothstep(0.6, 0.0, d) * uFocusStrength;

      vec3 col = mix(flatColor, mix(flatColor, vec3(1.0), 0.38), radialMask);

      col = col - filmGrainNoise(uv) * filmGrainIntensity;

      gl_FragColor = vec4(col, 1.0);
    }
  `;

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      uTopColor: { value: new THREE.Color() },
      uBottomColor: { value: new THREE.Color() },
      uAccentColor: { value: new THREE.Color() },
      uDarkColor: { value: new THREE.Color() },
      uFocusPoint: { value: new THREE.Vector2(0.5, 0.5) },
      uFocusStrength: { value: 0 },
    },
    vertexShader,
    fragmentShader: lavaLampFragmentShader
  });

  sceneMain.add(new THREE.Mesh(plane, material));

  const currentPalette = {
    top: new THREE.Color(),
    bottom: new THREE.Color(),
    accent: new THREE.Color(),
    dark: new THREE.Color(),
  };

  const targetPalette = {
    top: new THREE.Color(),
    bottom: new THREE.Color(),
    accent: new THREE.Color(),
    dark: new THREE.Color(),
  };

  const setPalette = (paletteObj, palette) => {
    paletteObj.top.setRGB(...palette.top);
    paletteObj.bottom.setRGB(...palette.bottom);
    paletteObj.accent.setRGB(...palette.accent);
    paletteObj.dark.setRGB(...palette.dark);
  };

  const applyColor = (hex, immediate = false) => {
    const palette = buildGradientColors(hex || defaultColor);

    setPalette(targetPalette, palette);

    if (immediate) {
      setPalette(currentPalette, palette);

      material.uniforms.uTopColor.value.copy(currentPalette.top);
      material.uniforms.uBottomColor.value.copy(currentPalette.bottom);
      material.uniforms.uAccentColor.value.copy(currentPalette.accent);
      material.uniforms.uDarkColor.value.copy(currentPalette.dark);
    }
  };

  const markCanvasReady = () => {
    if (window.__canvasBgReady) return;

    window.__canvasBgReady = true;
    document.documentElement.classList.add('canvas-bg-ready');
  };

  const resize = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    renderer.setSize(width, height, false);
    material.uniforms.uResolution.value.set(width, height);
  };

  const clock = new THREE.Clock();

  const renderFrame = () => {
    requestAnimationFrame(renderFrame);

    material.uniforms.uTime.value = clock.getElapsedTime();

    currentPalette.top.lerp(targetPalette.top, 0.06);
    currentPalette.bottom.lerp(targetPalette.bottom, 0.06);
    currentPalette.accent.lerp(targetPalette.accent, 0.06);
    currentPalette.dark.lerp(targetPalette.dark, 0.06);

    material.uniforms.uTopColor.value.copy(currentPalette.top);
    material.uniforms.uBottomColor.value.copy(currentPalette.bottom);
    material.uniforms.uAccentColor.value.copy(currentPalette.accent);
    material.uniforms.uDarkColor.value.copy(currentPalette.dark);

    renderer.render(sceneMain, orthoCamera);

    markCanvasReady();
  };

  applyColor(defaultColor, true);
  resize();
  renderFrame();

  window.addEventListener('resize', resize, { passive: true });

  document.addEventListener('mouseover', function (event) {
    const target = event.target.closest('[data-color]');

    if (!target) return;

    const color = resolveColor(target.dataset.color);

    if (color) {
      applyColor(color);
    }
  });

  document.addEventListener('mouseout', function (event) {
    const target = event.target.closest('[data-color]');

    if (!target) return;

    applyColor(defaultColor);
  });

 const resetColor = (immediate = false) => {
  applyColor(defaultColor, immediate);
};

window.skylrkGlobalCanvasBg = {
  applyColor,
  resetColor,
  resize
};

document.addEventListener('DOMContentLoaded', resetColor);

document.addEventListener('shopify:section:load', resetColor);

document.addEventListener('click', function () {
  setTimeout(function () {
    const isProductPage = document.querySelector('[id^="EpPdp-"]');

    if (!isProductPage && window.skylrkGlobalCanvasBg) {
      window.skylrkGlobalCanvasBg.resetColor();
    }
  }, 700);
});
})();